import { ArrowLeft, Plus, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Device {
  id: string;
  name: string;
  user: string;
  os: string;
  version: string;
  status: "active" | "prepared" | "inactive";
  serial: string;
  jssid: number;
  level?: string;
  installed_apps: string[];
  restrictions: Record<string, boolean>;
  hasProfile?: boolean;
}

interface App {
  id: string;
  name: string;
  bundleId: string;
  version?: string;
  icon_url?: string;
  category: string;
  tags: string[];
  system: boolean;
}

interface Level {
  id: string;
  name: string;
  description?: string;
  private: boolean;
  allowedApps: string[];
  blockedApps: string[];
  appFilter: Record<string, any>;
  restrictions: string[];
}

interface PageHeaderProps {
  title: string;
  onBack?: () => void;
  showBackButton?: boolean;
  showAddButton?: boolean;
  addButtonText?: string;
  onAddClick?: () => void;
  device?: Device;
  app?: App;
  level?: Level;
  backButtonText?: string;
  tooltipText?: string;
  hideTooltip?: boolean;
}

const getStatusColor = (status: string, hasProfile?: boolean) => {
  switch (status) {
    case "active":
      if (hasProfile === false) {
        return "bg-teal-100 text-teal-800";
      }
      return "bg-green-100 text-green-800";
    case "prepared":
      return "bg-yellow-100 text-yellow-800";
    case "inactive":
      return "bg-red-100 text-red-800";
    case "published":
    case "public":
      return "bg-green-100 text-green-800";
    case "draft":
    case "private":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusLabel = (status: string, hasProfile?: boolean) => {
  if (status === "active" && hasProfile === false) {
    return "active (no profile)";
  }
  return status;
};

export const PageHeader = ({
  title,
  onBack,
  showBackButton = false,
  showAddButton = false,
  addButtonText = "Add",
  onAddClick,
  device,
  app,
  level,
  backButtonText,
  tooltipText = "Manage users and their device access",
  hideTooltip = false,
}: PageHeaderProps) => {
  const renderHeaderContent = () => {
    if (device) {
      return (
        <div className="flex items-center space-x-3">
          <h1 className="text-xl font-semibold text-gray-900">{device.name}</h1>
          <Badge className={`${getStatusColor(device.status, device.hasProfile)} text-xs px-2 py-1`}>{getStatusLabel(device.status, device.hasProfile)}</Badge>
        </div>
      );
    }

    if (app) {
      return (
        <div className="flex items-center space-x-3">
          {app.icon_url && <img src={app.icon_url} alt={app.name} className="w-8 h-8 rounded-lg" />}
          <h1 className="text-xl font-semibold text-gray-900">{app.name}</h1>
          {app.version && <Badge className="bg-blue-100 text-blue-800 text-xs px-2 py-1">v{app.version}</Badge>}
        </div>
      );
    }

    if (level) {
      const levelStatus = level.private ? "private" : "public";
      return (
        <div className="flex items-center space-x-3">
          <h1 className="text-xl font-semibold text-gray-900">{level.name}</h1>
          <Badge className={`${getStatusColor(levelStatus)} text-xs px-2 py-1`}>{levelStatus}</Badge>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        {!hideTooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-gray-400 cursor-default" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  const getBackButtonText = () => {
    if (backButtonText) return backButtonText;
    if (level) return level.name;
    return "Back";
  };

  return (
    <div className="relative h-16">
      {/* Gray line that aligns with sidebar */}
      <div className="absolute top-4 left-0 right-0 h-px bg-gray-200"></div>

      {/* Header content with white background and bottom border */}
      <div className="relative bg-white h-fit border-b border-gray-200 flex items-center justify-between py-[0.5rem]">
        <div className="flex items-center gap-3 px-6">
          {showBackButton && onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 px-1 py-1 h-7"
            >
              <ArrowLeft className="w-3 h-3" />
              <span className="text-xs">{getBackButtonText()}</span>
            </Button>
          )}

          {renderHeaderContent()}
        </div>

        {showAddButton && onAddClick && (
          <div className="px-6">
            <Button onClick={onAddClick} className="flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>{addButtonText}</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
